/* @flow */

import type { CurrentInfoItem, Product } from "shop-state/types";

import React, { useEffect, useState } from "react";
import { useClient } from "entrypoint/shared";
import { currentInfoItem } from "queries";
import { useGetProductMeta, useGetNewsMeta } from "helpers/get-meta";
import { pointsPriceByID } from "helpers/points";
import { Helmet } from "react-helmet-async";
import { useTranslate } from "@awardit/react-use-translate";

type Props =
  | {
      type: "news",
      currentInfoItemID: string,
    }
  | {
      type: "product",
      productData: Product,
    };

const MetaNews = ({ newsData }: { newsData: CurrentInfoItem | null }): React$Node => {
  const meta = useGetNewsMeta(newsData);

  if (!meta) {
    return null;
  }

  return (
    <div>
      <Helmet title={meta.title} meta={meta.data} />
      <h1>Meta:</h1>
      <p>{meta.title}</p>
      <ul>
        {meta.data.map((item, index) => {
          return (
            <li key={index}>{item.property || item.name}: {typeof item.content === "string" ? item.content : ""}</li>
          );
        })}
      </ul>
    </div>
  );
};

const MetaProduct = ({ productData }: { productData: Product }): React$Node => {
  const t = useTranslate();
  const pointsPrice = pointsPriceByID(productData.pointsPrices, "awardit");
  const meta = useGetProductMeta(productData, t, pointsPrice);

  if (!meta) {
    return null;
  }

  return (
    <div>
      <Helmet title={meta.title} meta={meta.data} />
      <h1>Meta:</h1>
      <p>{meta.title}</p>
      <ul>
        {meta.data.map((item, index) => {
          return (
            <li key={index}>{item.property || item.name}: {String(item.content)}</li>
          );
        })}
      </ul>
    </div>
  );
};

const MetaView = (props: Props): React$Node => {
  const client = useClient();

  const [newsMetaData, setNewsMetaData] = useState(null);

  // Fetch news data only when in "news" mode
  useEffect(() => {
    if (props.type !== "news") {
      return;
    }

    const getCurrentInfoItem = async () => {
      await client(currentInfoItem, { id: String(props.currentInfoItemID) })
        .then(({ currentInfoItem }) => {
          if (currentInfoItem.result === "SUCCESS" && currentInfoItem.data) {
            setNewsMetaData(currentInfoItem.data);
          }
        });
    };

    getCurrentInfoItem();
  }, []);

  if (props.type === "news") {
    return <MetaNews newsData={newsMetaData} />;
  }

  return <MetaProduct productData={props.productData} />;
};

export default MetaView;
